import { PlusIcon } from "@phosphor-icons/react";
import type { ProjectSummary } from "../../generated/api/types.gen";
import { FolderCard } from "./FolderCard";
import { useSessionDropTarget } from "./useSessionDropTarget";

export interface ProjectWithDesignCount extends ProjectSummary {
  design_count: number;
}

interface GalleryFolderListProps {
  projects: ProjectWithDesignCount[];
  onProjectClick: (projectId: string) => void;
  onCreateFolder: () => void | Promise<void>;
  onMoveSessionToProject?: (
    sessionId: string,
    projectId: string | null,
  ) => void | Promise<void>;
}

export function GalleryFolderList({
  projects,
  onProjectClick,
  onCreateFolder,
  onMoveSessionToProject,
}: GalleryFolderListProps) {
  return (
    <div className="mb-8">
      {/* Heading is shown only when the project actually has child folders;
          with none, just the "Add New Folder +" card stands on its own. */}
      {projects.length > 0 && (
        <div className="mb-3 text-lg font-semibold text-[var(--text-primary)] leading-6">
          Folders
        </div>
      )}
      <div className="grid grid-cols-1 items-start gap-4 md:grid-cols-2 xl:grid-cols-3">
        <NewFolderGridCard
          onClick={() => {
            onCreateFolder();
          }}
        />
        {projects.map((project) => (
          <ProjectCard
            key={project.id}
            project={project}
            designCount={project.design_count}
            onClick={() => onProjectClick(project.id)}
            onMoveSessionToProject={onMoveSessionToProject}
          />
        ))}
      </div>
    </div>
  );
}

function NewFolderGridCard({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label="Create new folder"
      className="flex w-full items-center justify-center rounded-xl border border-dashed border-[var(--stroke-field)]/45 bg-transparent p-4 shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-xl"
    >
      <span className="inline-flex items-center gap-2 rounded-lg border border-[var(--accent-secondary-stroke)] px-4 py-2 text-sm font-semibold text-[var(--text-primary)] transition bg-[var(--accent-primary)]/25">
        Add New Folder
        <PlusIcon size={14} weight="bold" aria-hidden />
      </span>
    </button>
  );
}

interface ProjectCardProps {
  project: ProjectSummary;
  designCount: number;
  onClick: () => void;
  onMoveSessionToProject?: (
    sessionId: string,
    projectId: string | null,
  ) => void | Promise<void>;
}

function ProjectCard({
  project,
  designCount,
  onClick,
  onMoveSessionToProject,
}: ProjectCardProps) {
  const { isOver, isForbidden, dropProps } =
    useSessionDropTarget<HTMLButtonElement>(
      project.id,
      onMoveSessionToProject,
      { isOrgShared: project.is_shared_with_organization },
    );

  return (
    <FolderCard
      name={project.name}
      subtitle={`${designCount} Design${designCount !== 1 ? "s" : ""}`}
      onClick={onClick}
      dropProps={dropProps}
      isOver={isOver}
      isForbidden={isForbidden}
    />
  );
}
